"use client";

import * as React from "react";
import { Popover } from "@base-ui/react/popover";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { CalendarDays, ChevronDownIcon, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { TRIGGER_CLASS } from "@/components/ui/time-field";

export type DateFieldProps = {
    id?: string;
    /** "YYYY-MM-DD", or "" when not chosen yet — the same contract as <input type="date">. */
    value: string;
    onValueChange: (next: string) => void;
    disabled?: boolean;
    className?: string;
    /** Allow picking a day before today (e.g. editing a past event). Default false. */
    allowPast?: boolean;
};

/** "2026-10-23" → local-midnight Date (never `new Date(str)`, which parses as UTC). */
export function parseIsoDate(value: string): Date | undefined {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    if (!m) return undefined;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Local calendar day → "YYYY-MM-DD". */
export function toIsoDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** "in 3 days", "tomorrow", "2 weeks ago" — a quick sanity check under the field. */
export function relativeDayLabel(target: Date, today: Date = startOfToday()): string {
    const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    if (days === -1) return "Yesterday";
    const abs = Math.abs(days);
    const span = abs >= 14 ? `${Math.round(abs / 7)} weeks` : `${abs} days`;
    return days > 0 ? `In ${span}` : `${span} ago`;
}

const PRESETS: { label: string; days: number }[] = [
    { label: "Today", days: 0 },
    { label: "Tomorrow", days: 1 },
    { label: "In 1 week", days: 7 },
    { label: "In 2 weeks", days: 14 },
];

/**
 * The event date picker: a calendar popover (react-day-picker) in the same
 * rounded shell as `TimeField`/`DurationField`, so the three "When" inputs read
 * as one set. Replaces the browser's native date input, whose look differs per
 * browser and whose popup cannot be styled.
 */
export function DateField({ id, value, onValueChange, disabled, className, allowPast = false }: DateFieldProps) {
    const [open, setOpen] = React.useState(false);
    const selected = parseIsoDate(value);
    const today = startOfToday();
    const pastAllowed = allowPast || (!!selected && selected < today);

    const pick = (d: Date | undefined) => {
        if (!d) return;
        onValueChange(toIsoDate(d));
        setOpen(false);
    };

    const display = selected
        ? selected.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
        : null;

    return (
        <div className={cn("flex flex-col gap-1.5", className)}>
            <div
                className={cn(
                    "flex items-center gap-2 p-1.5 bg-white rounded-2xl border transition-all shadow-xs min-w-0",
                    "border-slate-200 hover:border-teal-700/40 focus-within:border-teal-700 focus-within:ring-2 focus-within:ring-teal-700/20",
                    open && "border-teal-700 ring-2 ring-teal-700/20",
                    disabled && "opacity-60 bg-slate-50 cursor-not-allowed",
                )}
            >
                <div className="pl-1 shrink-0">
                    <div
                        className={cn(
                            "size-9 rounded-full bg-teal-50/50 border-[2.2px] border-[#003d45] flex items-center justify-center transition-transform duration-300",
                            open && "rotate-[-8deg] scale-110",
                        )}
                    >
                        <CalendarDays className="size-4 text-[#003d45]" aria-hidden />
                    </div>
                </div>

                <Popover.Root open={open} onOpenChange={setOpen}>
                    <Popover.Trigger id={id} disabled={disabled} aria-label="Event date" className={TRIGGER_CLASS}>
                        <span className={cn("font-bold truncate", !display && "text-muted-foreground font-normal")}>
                            {display ?? "Select a date"}
                        </span>
                        <ChevronDownIcon
                            className={cn("size-4 text-muted-foreground transition-transform duration-200", open && "rotate-180")}
                        />
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Positioner sideOffset={8} align="start" className="z-50">
                            <Popover.Popup
                                className={cn(
                                    "cmt-date-popup rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl outline-none",
                                    "origin-[var(--transform-origin)] transition-[transform,opacity] duration-200 ease-out",
                                    "data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[starting-style]:-translate-y-1",
                                    "data-[ending-style]:scale-95 data-[ending-style]:opacity-0",
                                )}
                            >
                                <DayPicker
                                    mode="single"
                                    selected={selected}
                                    onSelect={pick}
                                    defaultMonth={selected ?? today}
                                    disabled={pastAllowed ? undefined : { before: today }}
                                    showOutsideDays
                                    animate
                                    captionLayout="dropdown"
                                    startMonth={new Date(today.getFullYear() - 1, 0)}
                                    endMonth={new Date(today.getFullYear() + 3, 11)}
                                />
                                <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1.5">
                                    {PRESETS.map((p) => {
                                        const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + p.days);
                                        const active = !!selected && toIsoDate(d) === value;
                                        return (
                                            <button
                                                key={p.label}
                                                type="button"
                                                onClick={() => pick(d)}
                                                className={cn(
                                                    "px-2.5 py-1 rounded-full text-xs font-semibold border transition-all",
                                                    active
                                                        ? "bg-[#003d45] text-white border-[#003d45]"
                                                        : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-900",
                                                )}
                                            >
                                                {p.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Popover.Popup>
                        </Popover.Positioner>
                    </Popover.Portal>
                </Popover.Root>
            </div>

            {selected && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold bg-teal-50 text-teal-900 border-teal-200 animate-in fade-in slide-in-from-top-1 duration-200">
                    <Sparkles className="size-3 text-teal-600" aria-hidden />
                    <span>
                        <strong>{relativeDayLabel(selected, today)}</strong>
                        {" · "}
                        {selected.toLocaleDateString("en-US", { weekday: "long" })}
                    </span>
                </div>
            )}
        </div>
    );
}

export default DateField;
